import {
  Button,
  makeStyles,
  mergeClasses,
  tokens,
  Text,
} from "@fluentui/react-components";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { ISignForm } from "../../interfaces/ISignForm";
import { ISignGenerate } from "../../interfaces/ISignGenerate";
import { Reflex } from "../../interfaces/ISignForm";
import { getPriceBreakdown } from "../../helper/pricing";
import { Sign } from "./Sign";
import { useTranslation } from "react-i18next";
type SignPreviewProps = {
  signForm: UseFormReturn<ISignForm, any, ISignForm>;
  setGenerateSign: React.Dispatch<React.SetStateAction<ISignGenerate>>;
  setCheckoutModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};
const useStyles = makeStyles({
  signWrapper: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    margin: "56px",
    width: "100%",
    position: "sticky",
    top: "56px",
    zIndex: 2,
  },
  buttons: {
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    bottom: tokens.spacingVerticalL,
    right: "56px",
    alignItems: "flex-end",
    gap: tokens.spacingHorizontalS,
    zIndex: 10,
  },
  button: {
    color: tokens.colorBrandForeground1,
  },
  buttonMargin: {
    marginRight: tokens.spacingVerticalL,
  },
  flex: {
    display: "flex",
  },
  priceCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minWidth: "220px",
    boxShadow: tokens.shadow4,
  },
  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
  },
  priceRowText: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  priceDivider: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
  },
  priceDividerText: {
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase500,
  },
});
export const SignPreview = ({
  signForm,
  setGenerateSign,
  setCheckoutModalOpen,
}: SignPreviewProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { watch } = signForm;
  const sign = watch();
  const { width, height } = watch();
  const breakdown = getPriceBreakdown(sign);
  return (
    <>
      <div className={styles.signWrapper}>
        <Sign sign={sign} />
      </div>

      <div className={styles.buttons}>
        <div className={styles.priceCard}>
          <div className={styles.priceRow}>
            <Text className={styles.priceRowText}>{t("label.basePrice")}</Text>
            <Text className={styles.priceRowText}>{`${breakdown.basePrice} kr`}</Text>
          </div>
          {sign.reflex === Reflex.HIGH && (
            <div className={styles.priceRow}>
              <Text className={styles.priceRowText}>{`+ ${t("label.reflexSurcharge")}`}</Text>
              <Text className={styles.priceRowText}>{`${breakdown.reflexSurcharge} kr`}</Text>
            </div>
          )}
          {sign.graffitiProtection && (
            <div className={styles.priceRow}>
              <Text className={styles.priceRowText}>{`+ ${t("label.graffitiSurcharge")}`}</Text>
              <Text className={styles.priceRowText}>{`${breakdown.graffitiSurcharge} kr`}</Text>
            </div>
          )}
          <div className={styles.priceDivider}>
            <Text as="strong" className={styles.priceDividerText}>{t("unit.quantityPrice")}</Text>
            <Text as="strong" className={styles.priceDividerText}>{`${breakdown.total} kr`}</Text>
          </div>
        </div>
        <div className={styles.flex}>
          <Button
            appearance="secondary"
            size="large"
            className={mergeClasses(styles.buttonMargin, styles.button)}
            onClick={() =>
              setGenerateSign({ generate: false, defaultValues: null })
            }
          >
            {t("button.back")}
          </Button>
          <Button
            appearance="primary"
            size="large"
            onClick={() => setCheckoutModalOpen(true)}
            disabled={width === 0 || height === 0}
          >
            {t("button.order")}
          </Button>
        </div>
      </div>
    </>
  );
};
